import { useEffect, useState } from 'react';

import moment from 'moment-timezone';
import { removeHtmlTagsFromString } from 'utilitys/helpers/general';

import { UseBaseTable } from 'hooks/useBaseTable';

import { useProjectTreeStore } from 'store/projectContract.store';

import constants from 'components/common/Constants/constants';

import { UseRiskRegisterProvider } from 'components/widgets/RiskRegister/RiskRegister.provider';
import { exportCsvHeaders } from 'components/widgets/RiskRegister/RiskRegister.utils';

interface Props {
    ui: UseRiskRegisterProvider['ui'];
    api: UseRiskRegisterProvider['api'];
    table: UseBaseTable['table'];
}

export const useRiskRegisterLogic = ({ ui, api, table }: Props) => {
    const { project, contract } = useProjectTreeStore();
    const [rowCount, setRowCount] = useState<number>(0);
    useEffect(() => {
        table.setColumnFilters([
            { id: 'status', value: ui.state.status },
            { id: 'probability', value: ui.state.probability },
            { id: 'registerName', value: ui.state.search },
            { id: 'durationVariance', value: ui.state.risks },
            { id: 'tags', value: ui.state.tags },
        ]);
    }, [ui.state.status, ui.state.probability, ui.state.search, ui.state.risks, ui.state.tags]);

    useEffect(() => {
        table.setPagination((prev) => ({ ...prev, page: 1 }));
    }, [
        ui.state.projectId,
        ui.state.contractId,
        ui.state.sorting,
        ui.state.status,
        ui.state.probability,
        ui.state.search,
        ui.state.tags,
    ]);

    useEffect(() => {
        if (table.tableRef.current) {
            table.setTotalPages(table.tableRef.current.table.getPageCount());
            setRowCount(table.tableRef.current.table.getRowCount());
        }
    }, [api.riskRegister.data, table.columnFilters, table.pagination.size]);

    return {
        logic: {
            rowCount,
            exportCsv: {
                headers: exportCsvHeaders,
                rows: api.riskRegister.data?.riskList.map((row) => ({
                    registerName: row.name,
                    description: removeHtmlTagsFromString(row.details),
                    status: row.status,
                    probability: row.probability,
                    distribution: row.distribution,
                    impactType: row.impactType,
                    'durationVariance.min': row.durationVariance.min,
                    'durationVariance.likely': row.durationVariance.likely,
                    'durationVariance.max': row.durationVariance.max,
                    linkedActivities: 'whole-project',
                    segment: row.activitiesByKey.segment?.name ?? '',
                    assignee: `${row.assignee.firstName} ${row.assignee.lastName}`,
                })),
                fileName: `risk_register_${project?.title}_${contract?.title}_${moment().format(constants.formats.date.yearMonthDay)}`,
            },
        },
    };
};
